import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { API_BASE } from "../lib/api";
import { 
  CheckCircle2, 
  Briefcase, 
  CreditCard, 
  Users, 
  BarChart3, 
  FileText, 
  Shield,
  ArrowRight,
  Star,
  Zap,
  Globe
} from "lucide-react";

const API = API_BASE;

const features = [
  {
    icon: Users,
    title: "Gestion des Clients",
    description: "Suivez vos leads, prospects et clients. Convertissez-les facilement."
  },
  {
    icon: Briefcase,
    title: "Missions & Projets",
    description: "Créez des missions avec échéanciers de paiement automatiques."
  },
  {
    icon: CreditCard,
    title: "Facturation Intelligente",
    description: "Acomptes, mensualités, versements partiels. Tout est géré."
  },
  {
    icon: FileText,
    title: "Documents PDF",
    description: "Générez devis, proformas et factures professionnelles en un clic."
  },
  {
    icon: BarChart3,
    title: "Comptabilité OHADA",
    description: "Plan comptable gabonais intégré. Journal, Grand Livre, Balance."
  },
  {
    icon: Shield,
    title: "Multi-Entreprises",
    description: "Chaque client a son espace isolé et sécurisé."
  }
];

const pricingPlans = [
  {
    name: "Essai",
    price: "Gratuit",
    period: "30 jours",
    features: ["2 utilisateurs", "Toutes les fonctionnalités", "Support email"],
    highlighted: false,
    badge: null
  },
  {
    name: "Standard",
    price: "50 000",
    currency: "FCFA",
    period: "/mois",
    features: ["5 utilisateurs", "Toutes les fonctionnalités", "Support prioritaire", "Export PDF illimité"],
    highlighted: true,
    badge: "Populaire"
  },
  {
    name: "Enterprise",
    price: "150 000",
    currency: "FCFA",
    period: "/mois",
    features: ["Utilisateurs illimités", "API personnalisée", "Support dédié", "Formation incluse", "Personnalisation"],
    highlighted: false,
    badge: null
  }
];

export default function Landing({ onLogin }) {
  const navigate = useNavigate();
  const [showLogin, setShowLogin] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    nom: "",
    confirmPassword: ""
  });

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isRegistering) {
        if (formData.password !== formData.confirmPassword) {
          toast.error("Les mots de passe ne correspondent pas");
          setLoading(false);
          return;
        }
        const response = await axios.post(`${API}/auth/register`, {
          email: formData.email,
          password: formData.password,
          nom: formData.nom,
          role: "gestionnaire"
        });
        onLogin(response.data.user, response.data.token);
        toast.success("Compte créé ! Bienvenue dans votre essai de 30 jours.");
        navigate("/");
      } else {
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password
        });
        onLogin(response.data.user, response.data.token);
        toast.success("Connexion réussie");
        navigate("/");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur d'authentification");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0B]/80 backdrop-blur-xl border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                <span className="text-white font-bold text-lg">ID</span>
              </div>
              <div>
                <span className="font-bold text-white text-lg tracking-tight">IDEAL</span>
                <span className="text-zinc-500 text-sm ml-1">Consulting</span>
              </div>
            </div>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-zinc-400 hover:text-white transition-colors">Fonctionnalités</a>
              <a href="#pricing" className="text-zinc-400 hover:text-white transition-colors">Tarifs</a>
              <Button 
                variant="outline" 
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                onClick={() => { setIsRegistering(false); setShowLogin(true); }}
              >
                Connexion
              </Button>
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => { setIsRegistering(true); setShowLogin(true); }}
              >
                Essai gratuit
              </Button>
            </nav>
            <Button 
              className="md:hidden bg-blue-600 hover:bg-blue-700"
              onClick={() => setShowLogin(true)}
            >
              Connexion
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <Badge className="bg-blue-600/20 text-blue-400 border-blue-600/30 mb-6">
              <Zap className="w-3 h-3 mr-1" />
              Solution complète de gestion
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Gérez vos missions et
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600"> facturations</span>
              <br />en toute simplicité
            </h1>
            <p className="text-lg sm:text-xl text-zinc-400 mb-8 max-w-2xl mx-auto">
              IDEAL Consulting vous propose une solution moderne pour gérer vos clients, missions, paiements et comptabilité selon les normes OHADA.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-blue-600 hover:bg-blue-700 text-lg px-8"
                onClick={() => { setIsRegistering(true); setShowLogin(true); }}
              >
                Commencer l'essai gratuit
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-lg px-8"
                onClick={() => { setIsRegistering(false); setShowLogin(true); }}
              >
                Se connecter
              </Button>
            </div>
            <p className="text-zinc-500 text-sm mt-4">
              <CheckCircle2 className="w-4 h-4 inline mr-1 text-emerald-500" />
              30 jours d'essai gratuit · Aucune carte requise
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20 max-w-4xl mx-auto">
            {[
              { value: "500+", label: "Entreprises" },
              { value: "10K+", label: "Missions gérées" },
              { value: "99.9%", label: "Disponibilité" },
              { value: "24/7", label: "Support" }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-3xl sm:text-4xl font-bold text-white">{stat.value}</p>
                <p className="text-zinc-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-zinc-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-emerald-600/20 text-emerald-400 border-emerald-600/30 mb-4">
              Fonctionnalités
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Tout ce dont vous avez besoin
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Une suite complète d'outils pour gérer votre activité de conseil et services.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <Card key={i} className="bg-[#121214] border-zinc-800 hover:border-zinc-700 transition-colors">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-blue-600/20 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-zinc-400 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-amber-600/20 text-amber-400 border-amber-600/30 mb-4">
              Tarifs
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Des tarifs adaptés à votre croissance
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Commencez gratuitement, évoluez selon vos besoins.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {pricingPlans.map((plan, i) => (
              <Card 
                key={i} 
                className={`relative bg-[#121214] ${
                  plan.highlighted 
                    ? 'border-blue-600 ring-1 ring-blue-600' 
                    : 'border-zinc-800'
                }`}
              >
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-blue-600 text-white">
                      <Star className="w-3 h-3 mr-1" />
                      {plan.badge}
                    </Badge>
                  </div>
                )}
                <CardContent className="p-8">
                  <h3 className="text-xl font-semibold text-white mb-2">{plan.name}</h3>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-white">{plan.price}</span>
                    {plan.currency && <span className="text-zinc-400 ml-1">{plan.currency}</span>}
                    <span className="text-zinc-500 ml-1">{plan.period}</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, j) => (
                      <li key={j} className="flex items-center text-zinc-300">
                        <CheckCircle2 className="w-5 h-5 text-emerald-500 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className={`w-full ${
                      plan.highlighted 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300'
                    }`}
                    onClick={() => { setIsRegistering(true); setShowLogin(true); }}
                  >
                    {plan.price === "Gratuit" ? "Commencer" : "Choisir ce plan"}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-600/10 to-transparent">
        <div className="max-w-4xl mx-auto text-center">
          <Globe className="w-16 h-16 text-blue-400 mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Prêt à transformer votre gestion ?
          </h2>
          <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">
            Rejoignez des centaines d'entreprises qui font confiance à IDEAL Consulting pour gérer leurs missions et facturations.
          </p>
          <Button 
            size="lg" 
            className="bg-blue-600 hover:bg-blue-700 text-lg px-8"
            onClick={() => { setIsRegistering(true); setShowLogin(true); }}
          >
            Démarrer maintenant
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-md bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                <span className="text-white font-bold text-sm">ID</span>
              </div>
              <span className="font-semibold text-white">IDEAL Consulting</span>
            </div>
            <p className="text-zinc-500 text-sm">
              © 2024 IDEAL Consulting. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>

      {/* Login/Register Dialog */}
      <Dialog open={showLogin} onOpenChange={setShowLogin}>
        <DialogContent className="bg-[#121214] border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white">
              {isRegistering ? "Créer un compte" : "Connexion"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAuth} className="space-y-4 mt-4">
            {isRegistering && (
              <div className="space-y-2">
                <Label className="text-zinc-400">Nom complet</Label>
                <Input
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  required={isRegistering}
                  className="bg-zinc-900/50 border-zinc-800"
                  placeholder="Jean Dupont"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-zinc-400">Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="bg-zinc-900/50 border-zinc-800"
                placeholder="email@exemple.com"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Mot de passe</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="bg-zinc-900/50 border-zinc-800"
                placeholder="••••••••"
              />
            </div>
            {isRegistering && (
              <div className="space-y-2">
                <Label className="text-zinc-400">Confirmer le mot de passe</Label>
                <Input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required={isRegistering}
                  className="bg-zinc-900/50 border-zinc-800"
                  placeholder="••••••••"
                />
              </div>
            )}
            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? "Chargement..." : (isRegistering ? "Créer mon compte" : "Se connecter")}
            </Button>
            <p className="text-center text-zinc-500 text-sm">
              {isRegistering ? (
                <>
                  Déjà un compte ?{" "}
                  <button 
                    type="button" 
                    className="text-blue-400 hover:underline"
                    onClick={() => setIsRegistering(false)}
                  >
                    Se connecter
                  </button>
                </>
              ) : (
                <>
                  Pas encore de compte ?{" "}
                  <button 
                    type="button" 
                    className="text-blue-400 hover:underline"
                    onClick={() => setIsRegistering(true)}
                  >
                    Créer un compte
                  </button>
                </>
              )}
            </p>
            {isRegistering && (
              <p className="text-center text-zinc-600 text-xs">
                En créant un compte, vous bénéficiez de 30 jours d'essai gratuit.
              </p>
            )}
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
